/**
 * Container Component
 *
 * A layout primitive for constraining content width with consistent,
 * responsive padding using design tokens.
 *
 * Features:
 * - Size variants from xs (320px) to full width
 * - Responsive horizontal padding (mobile-first)
 * - Optional vertical padding for page sections
 * - Semantic element support (div, section, main, article)
 * - No arbitrary values - all sizes from design tokens
 *
 * @example Basic usage
 * ```tsx
 * <Container>
 *   <Heading>Page Title</Heading>
 *   <Content />
 * </Container>
 * ```
 *
 * @example Size variants
 * ```tsx
 * <Container size="narrow">Narrow content (672px max)</Container>
 * <Container size="default">Default content (1024px max)</Container>
 * <Container size="wide">Wide content (1280px max)</Container>
 * ```
 *
 * @example With responsive padding
 * ```tsx
 * <Container padding="lg" paddingY="xl">
 *   Full page section with generous padding
 * </Container>
 * ```
 */

import * as React from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import {containerVariants} from './Container.variants';

// =============================================================================
// TYPES
// =============================================================================

interface ContainerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof containerVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: "div" | "section" | "article" | "main" | "aside";
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Container - Centered max-width layout primitive
 *
 * Use Container to constrain content width and add consistent padding.
 * Pages and features MUST use this instead of ad-hoc wrapper divs.
 *
 * Replaces patterns like:
 * - `className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12"`
 * - `className="container mx-auto px-4"`
 */
const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  function Container(
    {
      as: Component = "div",
      size,
      padding,
      paddingY,
      center,
      className,
      children,
      ...props
    },
    ref
  ): React.ReactElement {
    return (
      <Component
        ref={ref}
        className={cn(
          containerVariants({ size, padding, paddingY, center }),
          className
        )}
        data-layout="container"
        data-size={size ?? "default"}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Container.displayName = "Container";

// =============================================================================
// EXPORTS
// =============================================================================


export { Container,  };
